import { liveQuery } from 'dexie';
import { useEffect, useRef, useState } from 'react';

/**
 * Subscribes a component to a LocalStore query.
 *
 * Section 14 requires that the interface observes LocalStore and does not keep
 * a second authoritative message array fed straight from the socket. This hook
 * is how that rule is kept: every screen reads through it, so the only path
 * from the network to the screen runs through a durable local transaction.
 *
 * Dexie's `liveQuery` re-runs the querier whenever a table it touched changes,
 * including changes made in the sync worker — IndexedDB change notifications
 * are broadcast across contexts of the same origin — which is what lets the
 * worker own all writes without the UI polling.
 */
export function useLiveQuery<T>(
  querier: () => Promise<T>,
  deps: readonly unknown[],
  initial: T,
): T {
  const [value, setValue] = useState<T>(initial);

  // The querier is captured in a ref so that an inline arrow function does not
  // tear down and rebuild the subscription on every render. The dependency
  // array is the explicit statement of when the query actually changed.
  const querierRef = useRef(querier);
  querierRef.current = querier;

  useEffect(() => {
    let active = true;
    const subscription = liveQuery(() => querierRef.current()).subscribe({
      next: (next) => {
        if (active) {
          setValue(next);
        }
      },
      error: (error: unknown) => {
        // A failing read is reported once rather than retried in a loop: the
        // usual cause is a database that could not be opened at all, and a
        // retry loop would hide that behind a spinner forever.
        console.error('local store query failed', error);
      },
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return value;
}
